import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Descriptions, Tag, Space, message, Spin, Alert, List, Empty, Input } from 'antd';
import { SyncOutlined, ReloadOutlined, RobotOutlined, KeyOutlined, CheckOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';

export default function RpaSyncPanel({ open, onClose }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cookieStr, setCookieStr] = useState('');
  const [savingCookie, setSavingCookie] = useState(false);
  const [goodsCookieStr, setGoodsCookieStr] = useState('');
  const [savingGoodsCookie, setSavingGoodsCookie] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [report, setReport] = useState(null);
  const [logs, setLogs] = useState([]);
  // 远程浏览器登录
  const [remoteOpen, setRemoteOpen] = useState(false);
  const [shot, setShot] = useState(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const shotTimer = useRef(null);
  const imgRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const r = await apiFetch('/api/rpa/status').then(r => r.json());
      setStatus(r.data || null);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadStatus();
    apiFetch('/api/rpa/logs').then(r => r.json()).then(d => setLogs(d.data || [])).catch(() => {});
  }, [open, loadStatus]);

  const saveCookie = async () => {
    if (!cookieStr.trim()) { message.warning('请先粘贴 Cookie'); return; }
    setSavingCookie(true);
    try {
      const r = await apiFetch('/api/rpa/cookie', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cookie: cookieStr }) }).then(r => r.json());
      if (r.success) { message.success(`Cookie 已保存（${r.count} 项）`); setCookieStr(''); loadStatus(); }
      else message.error(r.error || '保存失败');
    } catch (e) { message.error('保存失败：' + e.message); }
    setSavingCookie(false);
  };

  // 商品页（yangkeduo.com）Cookie：给河伯打开竞品链接用
  const saveGoodsCookie = async () => {
    if (!goodsCookieStr.trim()) { message.warning('请先粘贴商品页 Cookie'); return; }
    setSavingGoodsCookie(true);
    try {
      const r = await apiFetch('/api/rpa/goods-cookie', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cookie: goodsCookieStr }) }).then(r => r.json());
      if (r.success) { message.success(`商品页 Cookie 已保存（${r.count} 项）`); setGoodsCookieStr(''); loadStatus(); }
      else message.error(r.error || '保存失败');
    } catch (e) { message.error('保存失败：' + e.message); }
    setSavingGoodsCookie(false);
  };

  // ===== 远程浏览器登录 =====
  const startRemote = async () => {
    setRemoteLoading(true);
    try {
      await apiFetch('/api/rpa/remote/start', { method: 'POST' }).then(r => r.json());
      setRemoteOpen(true);
      shotTimer.current = setInterval(async () => {
        try {
          const s = await apiFetch('/api/rpa/remote/shot').then(r => r.json()).catch(() => null);
          if (s && s.image) setShot(s.image);
          const c = await apiFetch('/api/rpa/remote/check').then(r => r.json()).catch(() => null);
          if (c && c.logged_in) {
            clearInterval(shotTimer.current);
            await apiFetch('/api/rpa/remote/stop', { method: 'POST' });
            setRemoteOpen(false); setShot(null);
            message.success('拼多多登录成功，会话已保存');
            loadStatus();
          }
        } catch (e) { /* ignore */ }
      }, 300);
    } catch (e) { message.error('启动失败：' + e.message); }
    setRemoteLoading(false);
  };
  const stopRemote = async () => {
    clearInterval(shotTimer.current);
    await apiFetch('/api/rpa/remote/stop', { method: 'POST' }).catch(() => {});
    setRemoteOpen(false); setShot(null);
  };
  const refreshQr = async () => {
    await apiFetch('/api/rpa/remote/refresh', { method: 'POST' }).catch(() => {});
    message.info('已刷新登录页，请稍候重新扫码');
  };
  const sendMouse = (action, e) => {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) * (1280 / rect.width));
    const y = Math.round((e.clientY - rect.top) * (800 / rect.height));
    apiFetch('/api/rpa/remote/mouse', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, x, y }) }).catch(() => {});
  };

  const doSync = async () => {
    setSyncing(true); setReport(null);
    try {
      const r = await apiFetch('/api/rpa/sync', { method: 'POST' }).then(r => r.json());
      if (r.success) {
        setReport(r.report);
        message.success('同步完成');
        apiFetch('/api/rpa/logs').then(r => r.json()).then(d => setLogs(d.data || [])).catch(() => {});
        loadStatus();
      } else message.error(r.error || '同步失败');
    } catch (e) { message.error('同步失败：' + e.message); }
    setSyncing(false);
  };

  return (
    <Modal title={<span><RobotOutlined style={{ color: 'var(--brand)' }} /> 拼多多售后同步（RPA）</span>}
      open={open} onCancel={onClose} footer={null} width={620}>
      {loading && <Spin />}
      {status && (
        <div style={{ marginTop: 12 }}>
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="① 在你电脑浏览器登录拼多多商家后台 → ② 复制 Cookie 粘贴到下方 → ③ 立即同步售后（去重、默认待处理、与人工并存）。" />
          <Descriptions size="small" column={2} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="平台">{status.platform}</Descriptions.Item>
            <Descriptions.Item label="RPA 环境">{status.installed ? <Tag color="green">已就绪</Tag> : <Tag color="red">未安装</Tag>}</Descriptions.Item>
            <Descriptions.Item label="Cookie 状态">{status.has_cookie ? <Tag color="green">已配置</Tag> : <Tag color="orange">未配置</Tag>}</Descriptions.Item>
            <Descriptions.Item label="商品页 Cookie">{status.has_goods_cookie ? <Tag color="green">已配置</Tag> : <Tag color="orange">未配置</Tag>}</Descriptions.Item>
            <Descriptions.Item label="最近同步">{status.last_sync ? status.last_sync.summary || status.last_sync.started_at : '—'}</Descriptions.Item>
          </Descriptions>

          {/* Cookie 注入区 */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, marginBottom: 4, fontWeight: 500 }}><KeyOutlined /> 粘贴拼多多 Cookie</div>
            <Input.TextArea rows={4} placeholder={'在拼多多商家后台页面按 F12 → 控制台(console)输入 document.cookie 回车 → 复制输出的整段内容粘贴到这里（name=value; name2=value2; ...）'}
              value={cookieStr} onChange={e => setCookieStr(e.target.value)} style={{ fontSize: 12, fontFamily: 'monospace' }} />
            <Space style={{ marginTop: 8 }} wrap>
              <Button type="primary" icon={<CheckOutlined />} loading={savingCookie} onClick={saveCookie}>保存 Cookie</Button>
              <Button icon={<ReloadOutlined />} onClick={loadStatus}>刷新状态</Button>
              <Button type="primary" icon={<SyncOutlined />} loading={syncing} onClick={doSync} disabled={!status.has_cookie}>立即同步售后</Button>
              <Button icon={<RobotOutlined />} loading={remoteLoading} onClick={startRemote}>远程浏览器登录（扫码）</Button>
            </Space>
          </div>

          {/* 商品页 Cookie 注入区（竞品浏览） */}
          <div style={{ marginBottom: 12, padding: 10, background: 'var(--brand-bg)', borderRadius: 8, border: '1px solid var(--brand-border)' }}>
            <div style={{ fontSize: 13, marginBottom: 4, fontWeight: 500 }}><KeyOutlined /> 商品页 Cookie（竞品浏览）</div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>
              给河伯看竞品链接用：在你手机/电脑浏览器打开任意拼多多商品页并登录后，F12 控制台输入 document.cookie 复制整段粘贴到这里。与上方「售后 Cookie」是两套登录态，需分开填。
            </div>
            <Input.TextArea rows={3} placeholder="商品页 cookie（name=value; name2=value2; ...）"
              value={goodsCookieStr} onChange={e => setGoodsCookieStr(e.target.value)} style={{ fontSize: 12, fontFamily: 'monospace' }} />
            <Space style={{ marginTop: 8 }} wrap>
              <Button type="primary" icon={<CheckOutlined />} loading={savingGoodsCookie} onClick={saveGoodsCookie}>保存商品页 Cookie</Button>
            </Space>
          </div>

          {report && (
            <Alert style={{ marginBottom: 12 }}
              type={report.errors && report.errors.length ? 'warning' : 'success'} showIcon
              message="同步完成" description={
                <div>
                  <div>{report.summary}</div>
                  <div style={{ marginTop: 4, fontSize: 12 }}>
                    {report.added.length > 0 && <span>🆕 新增 <b>{report.added.length}</b> 条</span>}
                    {report.skipped.length > 0 && <span style={{ marginLeft: 12 }}>⏭ 跳过（已存在）<b>{report.skipped.length}</b> 条</span>}
                    {report.errors.length > 0 && <span style={{ marginLeft: 12, color: '#cf1322' }}>⚠️ {report.errors.join('；')}</span>}
                  </div>
                </div>
              } />
          )}

          <div style={{ fontWeight: 600, margin: '8px 0' }}>同步历史</div>
          {logs.length ? (
            <List size="small" dataSource={logs.slice(0, 8)} locale={{ emptyText: '' }}
              renderItem={(l, i) => (
                <List.Item style={{ padding: '6px 0' }}>
                  <div style={{ width: '100%' }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{l.started_at} → {l.finished_at || ''}</div>
                    <div style={{ fontSize: 13 }}>{l.summary || l.errors.join('；') || '无数据'}</div>
                  </div>
                </List.Item>
              )} />
          ) : <Empty description="暂无同步记录" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '12px 0' }} />}
        </div>
      )}

      {/* 远程浏览器登录窗口 */}
      <Modal title={<span><RobotOutlined style={{ color: 'var(--brand)' }} /> 远程浏览器登录拼多多</span>}
        open={remoteOpen} onCancel={stopRemote} footer={null} width={960} destroyOnClose
        styles={{ body: { padding: 12 } }}>
        <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 8 }}>
          下方是拼多多登录页实时画面：直接点击/拖动操作（含滑块），扫码成功后自动登录。若提示二维码失效，点右上角「刷新二维码」。
        </div>
        <div style={{ background: '#f0f0f0', borderRadius: 8, overflow: 'hidden', position: 'relative', minHeight: 500, textAlign: 'center' }}>
          {shot ? (
            <img ref={imgRef} src={shot} alt="登录页" draggable={false}
              style={{ width: '100%', display: 'block', userSelect: 'none', cursor: 'crosshair' }}
              onMouseDown={(e) => sendMouse('down', e)}
              onMouseMove={(e) => { if (e.buttons & 1) sendMouse('move', e); }}
              onMouseUp={(e) => sendMouse('up', e)}
              onClick={(e) => sendMouse('click', e)} />
          ) : (
            <div style={{ padding: 80, color: '#8c8c8c' }}><Spin /> 正在加载拼多多登录页…</div>
          )}
        </div>
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button icon={<ReloadOutlined />} onClick={refreshQr}>刷新二维码</Button>
          <Button onClick={stopRemote}>关闭</Button>
        </div>
      </Modal>
    </Modal>
  );
}
